import React from 'react'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Truncate } from '@ui/Truncate/Truncate'
import { Body1 } from '@ui/Typography'

import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { useConversionRate } from '../hooks/useConversionRatesStore'
import { useCurrentPricesForTrades } from '../hooks/useCurrentPricesStore'
import type { PortfolioTrade } from '../hooks/useDxtradePortfolioTrades'
import { useTradePnl } from '../hooks/useTradePnl'
import { renderPnl } from '../utils/formatters'
import { calculatePnL } from '../utils/functions'

import styles from './TradePnL.module.scss'

type TypographyComponent = React.ComponentType<any>

type TradePnLProps = {
  trade: PortfolioTrade
  accountCurrency?: CurrencyType
  Typography?: TypographyComponent
}

export const TradePnL: React.FC<TradePnLProps> = ({
  trade,
  accountCurrency,
  Typography = Body1,
}) => {
  const { formatMoney } = useFormatNumber()
  const value = useTradePnl(trade, accountCurrency)
  return (
    <span className={styles.wrapper}>
      <SensitiveData>{renderPnl(value, Typography, accountCurrency, formatMoney)}</SensitiveData>
    </span>
  )
}

const computeTradePnL = (trade: PortfolioTrade, currentPrice?: number | null): number | null => {
  if (currentPrice == null) return null
  if (trade.side !== 'BUY' && trade.side !== 'SELL') return null
  if (typeof trade.entryPrice !== 'number') return null
  if (typeof trade.quantity !== 'number') return null

  return calculatePnL({
    side: trade.side,
    openPrice: trade.entryPrice,
    quantity: trade.quantity,
    exitPrice: currentPrice,
  })
}

type GroupPnLProps = {
  trades: PortfolioTrade[]
  accountCurrency?: CurrencyType
  Typography?: TypographyComponent
  wrapperStyles?: boolean
  className?: string
}

export const GroupPnL: React.FC<GroupPnLProps> = ({
  trades,
  accountCurrency,
  Typography = Body1,
  wrapperStyles = true,
  className,
}) => {
  const currentPrices = useCurrentPricesForTrades(trades, 'position')
  const instrumentCurrency = trades[0]?.instrumentCurrency
  const { rate, requiresConversion } = useConversionRate(instrumentCurrency, accountCurrency)

  const total = React.useMemo(() => {
    return trades.reduce<number>((acc, trade, index) => {
      const pnl = computeTradePnL(trade, currentPrices[index] ?? null)
      if (typeof pnl === 'number') return acc + pnl
      return acc
    }, 0)
  }, [currentPrices, trades])

  const convertedTotal = React.useMemo(() => {
    if (!requiresConversion) return total
    if (rate == null) return null
    return total * rate
  }, [total, requiresConversion, rate])

  const { formatMoney } = useFormatNumber()

  return (
    <span className={wrapperStyles ? styles.wrapper : undefined}>
      <SensitiveData>
        <Truncate>
          {renderPnl(convertedTotal, Typography, accountCurrency, formatMoney, className)}
        </Truncate>
      </SensitiveData>
    </span>
  )
}
